import { createApp, h, render } from 'vue'
import App from './App.vue'
import B2bUserInformationDialog from '@/components/B2bUserInformationDialog.vue'

import '@/plugins/tinymce-setup'
import ONEVue from '@one/one-vue'
import '@one/one-vue/theme/src/reset.scss'
import '@one/one-vue/theme/src/index.scss'
import '@one/one-vue/theme/src/helpers/index.scss'
import messageboxPatch from '@/plugins/messagebox-patch.js';


import info from '../package.json'
import i18n from './lang/i18n.js'
import { closest } from './utils/ie'

import '@/axios-config'
import router from './router'
import store from './store/store.js'
import Keycloak from 'keycloak-js'
import axios from 'axios'

import '@/assets/scss/main.scss'
import '@/assets/scss/tinymce-overlays.scss'

import { defineCustomElement as defineDatavizPie } from '@one/webapp-ui/dist/components/one-dataviz-pie.js'
import { defineCustomElement as defineDatavizBar } from '@one/webapp-ui/dist/components/one-dataviz-bar.js'

defineDatavizPie()
defineDatavizBar()

const app = createApp(App)

app.use(i18n)
app.use(router)
app.use(store)

app.use(ONEVue, { project: 'fenix-ux' })
app.use(messageboxPatch);

app.config.productionTip = false

// app.config.devtools = false

closest()

require('vue-moment')

// Devuelve el dia en formato dd/mm/yyyy
app.config.globalProperties.$getDay = function (date) {
  const d = new Date(date)
  return d.getDate() + '/' + d.getMonth() + '/' + d.getFullYear()
}
// Devuelve el dia en formato hh/mm/ss
app.config.globalProperties.$getTime = function (date) {
  const d = new Date(date)
  return d.getHours() + ':' + d.getMinutes() + ':' + d.getSeconds()
}

if (process.env.NODE_ENV === 'dev' || process.env.NODE_ENV === 'preproduction') {
  globalThis.oneInfo = {
    'one-version': ONEVue.version,
    project: info.name,
    author: info.author
  }
}

// Agrupa la detección de clientId
function detectClientId(pathname, hostname) {
  const map = [
    ['ree', 'redelectrica'],
    ['quiron', 'quiron'],
    ['hipoges', 'hipoges'],
    ['ohla', 'ohla'],
    ['renfe', 'renfe'],
    ['EECG', 'EECG'],
    ['CanalSA', 'CanalSA'],
    ['lyntia', 'lyntia'],
    ['correosexpress', 'correosexpress'],
    ['telefonica', 'telefonica'],
    ['accionaenergia', 'accionaenergia'],
    ['vocento', 'vocento'],
    ['red.es', 'red.es'],
    ['pacsa', 'pacsa']
  ];
  //si el hostname es el de ree o red.es lo forzamos
  if (hostname.includes("efactura.red.es")) {
  	console.log('vamos a red.es');
  	localStorage.setItem('client_id', 'red.es');
  	return 'red.es';
  } else if (hostname.includes("efactura")) {
  	console.log('vamos a ree');
  	localStorage.setItem('client_id', 'redelectrica');
  	return 'redelectrica';
  }
  for (const [key, value] of map) {
    if (pathname.toLowerCase().includes(`/${key.toLowerCase()}`)) {
      localStorage.setItem('client_id', value);
      return value;
    }
  }
  
  // Si no está en el pathname, verificar si es una ruta genérica (sin cliente específico)
  // Rutas genéricas: /einvoiceng, /einvoiceng/, /einvoiceng/login, /einvoiceng/login/, etc.
  const isGenericPath = pathname === '/' || 
                        pathname === '/einvoiceng' || 
                        pathname === '/einvoiceng/' ||
                        pathname.startsWith('/einvoiceng/login') ||
                        pathname === '/login' ||
                        pathname === '/login/';
  
  if (isGenericPath) {
    // Para rutas genéricas, NO recuperar de localStorage, forzar genérico
    localStorage.removeItem('client_id');
    return 'fenix-b2b';
  }
  
  // Para otras rutas internas, intentar recuperar de localStorage (útil para navegación interna después de login)
  const savedClientId = localStorage.getItem('client_id');
  if (savedClientId && savedClientId !== 'fenix-b2b') {
    return savedClientId;
  }
  
  // Si no hay nada en localStorage, borrar y retornar genérico
  localStorage.removeItem('client_id');
  return 'fenix-b2b';
}

function setupKeycloak(clientId) {
  return new Keycloak({
    url: process.env.VUE_APP_KEYCLOAK_URL,
    //url: 'http://localhost:8080/auth',
    realm: 'fenix',
    clientId
  });
}

function setupAxiosInterceptors(app, store, keycloak) {
  let tokenRefreshPromise = null;
  axios.defaults.headers.common.Authorization = 'Bearer ' + keycloak.token;

  axios.interceptors.request.use(async config => {
    const kc = app.config.globalProperties.$keycloak;
    if (kc?.token) {
      try {
        if (tokenRefreshPromise) {
          await tokenRefreshPromise;
          config.headers.Authorization = 'Bearer ' + kc.token;
          return config;
        }
        tokenRefreshPromise = kc.updateToken(300);
        const refreshed = await tokenRefreshPromise;
        tokenRefreshPromise = null;
        if (refreshed) {
          const newBearer = 'Bearer ' + kc.token;
          config.headers.Authorization = newBearer;
          axios.defaults.headers.common.Authorization = newBearer;
          store.commit('JWT_STATE', newBearer);
          console.log('Token refrescado por interceptor');
        } else {
          config.headers.Authorization = 'Bearer ' + kc.token;
        }
      } catch (error) {
        console.warn('Error al refrescar token:', error);
        tokenRefreshPromise = null;
        config.headers.Authorization = 'Bearer ' + kc.token;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    response => response,
    async error => {
      if (error.response?.status === 401) {
        console.warn('Error 401 detectado, intentando refrescar token...');
        const kc = app.config.globalProperties.$keycloak;
        if (kc) {
          try {
            const refreshed = await kc.updateToken(0);
            if (refreshed) {
              const newBearer = 'Bearer ' + kc.token;
              axios.defaults.headers.common.Authorization = newBearer;
              store.commit('JWT_STATE', newBearer);
              error.config.headers.Authorization = newBearer;
              return axios.request(error.config);
            } else {
              console.error('No se pudo refrescar el token, redirigiendo al login');
              setTimeout(() => {
                globalThis.location.reload();
              }, 5000);
            }
          } catch (refreshError) {
            console.error('Error al refrescar token:', refreshError);
            globalThis.location.reload();
          }
        }
      }
      throw error
    }
  );
}

function resolveDateValue(value, isEndDate) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const dateString = String(value).trim()
  if (!dateString) {
    return null
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return isEndDate
      ? new Date(`${dateString}T23:59:59.999`)
      : new Date(`${dateString}T00:00:00.000`)
  }

  const parsed = new Date(dateString)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function isDateWithinValidity(item) {
  const now = new Date()
  const from = resolveDateValue(item?.date_from ?? item?.dateFrom, false)
  const to = resolveDateValue(item?.date_to ?? item?.dateTo, true)

  if (from && now < from) {
    return false
  }

  if (to && now > to) {
    return false
  }

  return true
}

async function showUserInformationPopup(app) {
  if (store.getters.activeUser?.data?.passwordExpired) {
    return
  }
  try {
    const items = await store.dispatch('getUserInformation')
    const hiddenIds = await store.dispatch('getHiddenUserInformation').catch(() => [])
    const match = (items || []).find(item => isDateWithinValidity(item) && !hiddenIds.includes(item.id))

    if (match?.message && match.message.trim()) {
      const container = document.createElement('div')
      const vnode = h(B2bUserInformationDialog, {
        message: match.message.trim(),
        onClosed: (dontShowAgain) => {
          render(null, container)
          container.remove()
          if (dontShowAgain) {
            store.dispatch('saveUserInformationDisplay', { id: match.id, display: false })
              .catch(error => console.warn('No se pudo guardar el registro de visualización:', error))
          }
        }
      })
      vnode.appContext = app._context
      document.body.appendChild(container)
      render(vnode, container)
    }
  } catch (error) {
    console.warn('No se pudo cargar la información del usuario:', error)
  }
}

const pathname = globalThis.location.pathname;
const hostname = globalThis.location.hostname;
const clientId = detectClientId(pathname, hostname);
app.config.globalProperties.$apiBaseUrl = clientId === 'fenix-b2b' ? 'b2b' : clientId;

// Esto asegura que al hacer reload (Ctrl+F5) se mantenga el branding personalizado
const urlInValue = clientId === 'fenix-b2b' ? 'b2b' : clientId;
store.commit('URL_IN_STATE', urlInValue);

// Solo usar el idioma guardado en localStorage
let lang = localStorage.getItem('ui_locales');
if (lang === 'pt-BR') lang = 'pt';
if (lang && ['es', 'en', 'it', 'pt'].includes(lang)) {
  i18n.global.locale = lang;
  store.dispatch('changeActiveLangState', lang);
}

const isPublicRoute = pathname.includes('new-password-form') || pathname.toLowerCase().includes('link') || pathname.includes('/vocento/detail') || pathname.includes('/vocento/download') || pathname.includes('downloadPdfCmr');

if (isPublicRoute) {
  console.log('Ruta pública detectada, montando app');
  app.mount('#app');
} else {
  // Detectar si es el caso especial de /vocento?id=xxx (sin token) para preservar la URL
  const urlParams = new URLSearchParams(globalThis.location.search);
  const isVocentoPendingTask = pathname.includes('/vocento') && urlParams.has('id') && !urlParams.has('token');
  
  // Si es el caso especial, guardar el ID en sessionStorage ANTES de inicializar Keycloak
  if (isVocentoPendingTask) {
    const pendingTaskId = urlParams.get('id');
    sessionStorage.setItem('pendingTaskRedirectId', pendingTaskId);
  }
  
  const keycloak = setupKeycloak(clientId);
  app.config.globalProperties.$keycloak = keycloak;
  
  // Solo preservar la URL completa para el caso de tareas pendientes de vocento
  const keycloakConfig = { 
    onLoad: 'login-required', 
    checkLoginIframe: false
  };
  if (isVocentoPendingTask) {
    keycloakConfig.redirectUri = globalThis.location.href;
  }
  
  keycloak.init(keycloakConfig).then((auth) => {
    if (!auth) {
      console.log('No ha habido autenticación');
      globalThis.location.reload();
      return;
    }
    const bearerToken = 'Bearer ' + keycloak.token;
    store.commit('JWT_STATE', bearerToken);
    try {
      setupAxiosInterceptors(app, store, keycloak);
      store.dispatch('login', {
        username: keycloak.tokenParsed?.preferred_username ? keycloak.tokenParsed.preferred_username : '',
        password: null,
        urlIn: app.config.globalProperties.$apiBaseUrl
      }).then(() => {
        return store.dispatch('parseUserAuthorities');
      }).then(() => {
        app.mount('#app');
        router.push('/').catch(() => {});
        return showUserInformationPopup(app)
      }).catch(e => {
        console.error('Error en login SSO:', e);
      });
    } catch (e) {
      console.warn('No se pudo establecer el header Authorization en Axios:', e);
    }
  }).catch((error) => { // NOSONAR
    console.error('Error al inicializar Keycloak:', error);
  });
}
